// client/src/coms/e2ee/EncryptedReportDialog.js
// Reporting end-to-end encrypted messages. The person reporting picks which messages to include; only those are
// taken from this device's own decryption, shown in full, and sent after an explicit confirmation that they will be
// disclosed in readable form to the moderators. Nothing else of the conversation, and no keys, leave the device.
//
// messages: [{ id, author, text, ok, ciphertext, senderDevice, verified, fromBackup, unsignedDevice, mine }]
import React, { useMemo, useState } from 'react';
import { comsApiPost } from '../comsApi';
import './e2ee.css';

const REASONS = [
  ['harassment', 'Harassment or bullying'],
  ['violence', 'Threats or violence'],
  ['spam', 'Spam or scams'],
  ['hate', 'Hate speech'],
  ['sexual', 'Sexual content'],
  ['self_harm', 'Self-harm'],
  ['illegal', 'Illegal content'],
  ['other', 'Something else'],
];

export default function EncryptedReportDialog({ scope, conversationId, messages, initialId, onClose }) {
  const reportable = useMemo(() => (messages || []).filter((m) => m && m.ok && m.ciphertext), [messages]);
  const [selected, setSelected] = useState(() => new Set(initialId != null ? [String(initialId)] : []));
  const [reason, setReason] = useState('harassment');
  const [details, setDetails] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const chosen = reportable.filter((m) => selected.has(String(m.id)));
  const audience = scope === 'dm' ? 'Voxemera’s safety moderators' : 'this server’s moderators';

  const toggle = (id) => {
    setConfirmed(false);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(String(id))) next.delete(String(id));
      else if (next.size < 20) next.add(String(id));
      return next;
    });
  };

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await comsApiPost('/reports/e2ee', {
        scope,
        ...(scope === 'dm' ? { thread_id: Number(conversationId) } : { channel_id: Number(conversationId) }),
        reason,
        details,
        confirm_disclosure: true,
        items: chosen.map((m) => ({
          message_id: Number(m.id),
          plaintext: m.text,
          ciphertext: m.ciphertext,
          // Empty only for history restored from the key backup that no device has re-confirmed (the server notes it).
          sender_device_id: m.senderDevice || '',
          reporter_device: { verified: !!m.verified, from_backup: !!m.fromBackup, unsigned_device: !!m.unsignedDevice },
        })),
      });
      setDone(r);
    } catch (e) {
      setError(String((e && e.data && e.data.error) || (e && e.message) || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="vx-e2ee-modal-backdrop" role="presentation">
      <div className="vx-e2ee-modal" role="dialog" aria-modal="true" aria-label="Report encrypted messages" data-testid="e2ee-report">
        {done ? (
          <>
            <h3 className="vx-e2ee-modal-title">Report sent</h3>
            <p>
              {audience.charAt(0).toUpperCase() + audience.slice(1)} will review it. You can follow its status in Personal Settings → Privacy &amp; Security.
              The conversation itself stays end-to-end encrypted.
            </p>
            <div className="vx-e2ee-confirm-row">
              <button type="button" className="vx-btn small" onClick={onClose} data-testid="e2ee-report-close">
                Close
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 className="vx-e2ee-modal-title">Report encrypted messages</h3>
            <p>
              Choose the messages to report. Only the messages you select are decrypted on this device and sent, in readable form, to {audience}. Nothing
              else in this conversation is shared, and the conversation stays encrypted.
            </p>
            <div className="vx-e2ee-report-list" data-testid="e2ee-report-list">
              {reportable.length ? (
                reportable.map((m) => (
                  <label key={m.id} className={`vx-e2ee-report-item${selected.has(String(m.id)) ? ' is-on' : ''}`}>
                    <input type="checkbox" checked={selected.has(String(m.id))} onChange={() => toggle(m.id)} data-testid={`e2ee-report-pick-${m.id}`} />
                    <span>
                      <strong>{m.author}</strong>
                      {m.mine ? ' (you)' : ''}: {m.text}
                      {m.fromBackup ? <em className="vx-set-hint"> (restored from your key backup, sender not confirmed)</em> : null}
                    </span>
                  </label>
                ))
              ) : (
                <span className="vx-set-hint">No messages this device can decrypt are loaded.</span>
              )}
            </div>
            <label className="vx-e2ee-report-field">
              Reason{' '}
              <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="e2ee-report-reason">
                {REASONS.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
            <textarea
              className="vx-e2ee-input"
              rows={2}
              maxLength={1000}
              placeholder="Anything moderators should know (optional)"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
            />
            {chosen.length ? (
              <div className="vx-e2ee-ack" data-testid="e2ee-report-disclosure">
                <strong>
                  These {chosen.length} message{chosen.length === 1 ? '' : 's'} will be disclosed in readable form to {audience}:
                </strong>
                {chosen.map((m) => (
                  <span key={m.id}>
                    “{m.text}” from {m.author}
                  </span>
                ))}
                <span>
                  {audience.charAt(0).toUpperCase() + audience.slice(1)} will see: the readable text of these messages, who sent each one and when, your account
                  name, the reason and your note. They do not get any encryption keys or any other message, and the conversation stays end-to-end encrypted.
                </span>
                <span>
                  Voxemera checks that these encrypted messages exist and who sent them from which device, but it cannot prove the readable text, so moderators
                  see it as your account of what the messages say.
                </span>
                <label>
                  <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} data-testid="e2ee-report-confirm" /> I understand and want to
                  disclose these messages
                </label>
              </div>
            ) : null}
            {error ? (
              <div className="vx-acct-error" role="alert">
                {error}
              </div>
            ) : null}
            <div className="vx-e2ee-confirm-row">
              <button type="button" className="vx-btn small danger" disabled={busy || !confirmed || !chosen.length} onClick={submit} data-testid="e2ee-report-submit">
                {busy ? 'Sending…' : 'Send report'}
              </button>
              <button type="button" className="vx-btn small" disabled={busy} onClick={onClose}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
